/**
 * GameEndScreen.js - Game End Screen Management
 *
 * Handles:
 * - Victory/Defeat screen display
 * - Player statistics display
 * - Return to lobby functionality
 * - This match's per-weapon breakdown (stats phase)
 */

import { renderWeaponTable } from './weaponTable.js';
import { renderAbilityTable } from './abilityTable.js';

const GameEndScreen = {
  // Callbacks for game actions
  callbacks: {
    onReturnToLobby: null,
    onSpectate: null,
    onSubmitFlagNote: null
  },

  // The data last passed to show(), so the "View Recap" badge can re-show it
  // without the caller having to remember and resend it.
  _lastData: null,

  /**
   * Initialize the game end screen
   * @param {Object} options - Configuration options
   * @param {Object} options.callbacks - Callback functions
   */
  init(options = {}) {
    Object.assign(this.callbacks, options.callbacks || {});
  },

  /**
   * Show the game end screen
   * @param {Object} data - Game end data
   * @param {boolean} data.victory - Whether the player won
   * @param {string} data.winnerName - Name of the winner
   * @param {string} data.killedByName - Name of player who killed us (if applicable)
   * @param {string} data.killedBy - Id of the player who killed us (spectate target)
   * @param {'eliminated'|'matchEnd'} [data.phase] - 'eliminated' offers Spectate
   *   (the match is still going for everyone else); anything else (or omitted,
   *   for back-compat) is treated as the match being over for good.
   * @param {number} data.eliminations - Number of eliminations
   * @param {number} data.damageDealt - Total damage dealt
   * @param {number} [data.damageBlocked] - Damage defensive abilities blocked this match
   * @param {Array<{abilityId: string, name: string, damageBlocked: number}>} [data.abilities]
   *   Per-ability blocked rows. On match end they ride GAME_END; on mid-match
   *   elimination they ride playerDied. Absent on older payloads.
   * @param {Array<{weaponId: string, name: string, damageDealt: number, kills: number}>} [data.weapons]
   *   This match's per-weapon breakdown, server-sorted by damage. It rides on
   *   the GAME_END payload, so the recap costs no round trip. Absent on older
   *   payloads; empty when the player dealt no attributable damage.
   */
  show(data) {
    // Remove existing end screen if present
    this.hide();
    this._hideSpectateBadge();
    this._lastData = data;

    // Create game end overlay
    const gameEndScreen = document.createElement('div');
    gameEndScreen.id = 'gameEndScreen';
    gameEndScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 2000;
      color: white;
      font-family: Arial, sans-serif;
    `;

    // Title - Victory or Defeat
    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 64px;
      font-weight: bold;
      margin-bottom: 30px;
      text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.8);
      ${data.victory ? 'color: #00ff00;' : 'color: #ff4444;'}
    `;
    title.textContent = data.victory ? '🏆 VICTORY!' : '💀 DEFEATED';
    gameEndScreen.appendChild(title);

    // Winner/elimination message
    const winnerText = document.createElement('div');
    winnerText.style.cssText = `
      font-size: 24px;
      margin-bottom: 40px;
      color: #ddd;
    `;

    // Determine message based on game state
    if (data.victory && data.winningTeam == null) {
      winnerText.textContent = 'You are the last player standing!';
    } else if (data.winnerName) {
      // Game has ended, we know the winner
      winnerText.textContent = `${data.winnerName} wins!`;
    } else if (data.killedByName) {
      // Died mid-game, show who killed us
      winnerText.textContent = `Eliminated by ${data.killedByName}`;
    } else {
      // Generic defeat message
      winnerText.textContent = 'You have been eliminated';
    }

    gameEndScreen.appendChild(winnerText);

    // Stats container
    const statsContainer = document.createElement('div');
    statsContainer.style.cssText = `
      background: rgba(50, 50, 50, 0.8);
      padding: 30px 50px;
      border-radius: 10px;
      margin-bottom: 40px;
      border: 2px solid #666;
    `;

    const stats = document.createElement('div');
    stats.style.cssText = `
      font-size: 20px;
      line-height: 2;
      color: #fff;
    `;
    stats.innerHTML = `
      <div><strong>Eliminations:</strong> ${data.eliminations || 0}</div>
      <div><strong>Damage Dealt:</strong> ${Math.round(data.damageDealt || 0)}</div>
      <div><strong>Damage Blocked:</strong> ${Math.round(data.damageBlocked || 0)}</div>
    `;
    statsContainer.appendChild(stats);

    // Per-weapon breakdown — the same renderer the account screen uses, over
    // data the GAME_END payload already carried (no round trip).
    if (Array.isArray(data.weapons)) {
      const heading = document.createElement('div');
      heading.style.cssText = 'margin-top: 18px; font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #9aa0a6;';
      heading.textContent = 'Weapons';
      const table = renderWeaponTable(data.weapons, { emptyMessage: 'No weapon damage this match.' });
      table.style.cssText = 'margin-top: 8px; min-width: 320px;';
      statsContainer.append(heading, table);
    }

    // Per-ability "damage blocked" — same idea, same renderer as the account screen.
    if (Array.isArray(data.abilities)) {
      const heading = document.createElement('div');
      heading.style.cssText = 'margin-top: 18px; font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #9aa0a6;';
      heading.textContent = 'Damage blocked';
      const table = renderAbilityTable(data.abilities, { emptyMessage: 'No damage blocked this match.' });
      table.style.cssText = 'margin-top: 8px; min-width: 320px;';
      statsContainer.append(heading, table);
    }

    gameEndScreen.appendChild(statsContainer);

    if (data.rankedPending || data.ranked) {
      gameEndScreen.appendChild(this._buildRankedPanel(data.ranked, Boolean(data.rankedPending) && !data.ranked));
    }

    const flagsBlock = this._buildPendingFlags(data.pendingFlags);
    if (flagsBlock) gameEndScreen.appendChild(flagsBlock);

    // Action buttons
    const buttonRow = document.createElement('div');
    buttonRow.style.cssText = `display: flex; gap: 20px;`;

    // Return to Lobby button
    const returnButton = document.createElement('button');
    returnButton.textContent = 'Return to Lobby';
    returnButton.style.cssText = `
      padding: 20px 50px;
      font-size: 24px;
      font-weight: bold;
      background: #0066cc;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.2s;
    `;
    returnButton.onmouseover = () => returnButton.style.background = '#0088ff';
    returnButton.onmouseout = () => returnButton.style.background = '#0066cc';
    returnButton.onclick = () => {
      if (this.callbacks.onReturnToLobby) {
        this.callbacks.onReturnToLobby();
      }
    };
    buttonRow.appendChild(returnButton);

    // Spectate button - only while the match is still going on for everyone else
    if (data.phase === 'eliminated') {
      const spectateButton = document.createElement('button');
      spectateButton.id = 'gameEndSpectateBtn';
      spectateButton.textContent = 'Spectate';
      spectateButton.style.cssText = `
        padding: 20px 50px;
        font-size: 24px;
        font-weight: bold;
        background: #444;
        color: white;
        border: 2px solid #888;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.2s;
      `;
      spectateButton.onmouseover = () => spectateButton.style.background = '#555';
      spectateButton.onmouseout = () => spectateButton.style.background = '#444';
      spectateButton.onclick = () => {
        this.hide();
        this._showSpectateBadge();
        if (this.callbacks.onSpectate) {
          this.callbacks.onSpectate(data);
        }
      };
      buttonRow.appendChild(spectateButton);
    }

    gameEndScreen.appendChild(buttonRow);

    // Add to page
    document.body.appendChild(gameEndScreen);
  },

  /**
   * Show a brief victory celebration, then hand off to the normal recap screen.
   * @param {Object} data - Same shape as show(); phase is forced to 'matchEnd'.
   * @param {number} [autoDismissMs] - How long the popup stays before auto-advancing.
   */
  showVictory(data, autoDismissMs = 3000) {
    this.hide();
    this._hideSpectateBadge();

    const popup = document.createElement('div');
    popup.id = 'gameVictoryPopup';
    popup.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 2100;
      color: #ffd700;
      font-family: Arial, sans-serif;
      cursor: pointer;
    `;
    popup.innerHTML = `
      <div style="font-size: 80px; font-weight: bold; text-shadow: 3px 3px 6px rgba(0,0,0,0.8);">🏆 VICTORY!</div>
      <div style="font-size: 22px; color: #ddd; margin-top: 20px;">Click to see your recap</div>
    `;

    const advance = () => {
      clearTimeout(timer);
      popup.remove();
      this.show({ ...data, phase: 'matchEnd' });
    };
    popup.onclick = advance;
    const timer = setTimeout(advance, autoDismissMs);

    document.body.appendChild(popup);
  },

  /**
   * Hide/remove the game end screen
   */
  hide() {
    const existingScreen = document.getElementById('gameEndScreen');
    if (existingScreen) {
      existingScreen.remove();
    }
  },

  /**
   * Check if game end screen is visible
   * @returns {boolean}
   */
  isVisible() {
    return document.getElementById('gameEndScreen') !== null;
  },

  /**
   * Apply an async rankedResult. Always stored on _lastData so a later
   * View Recap sees it; only paints if the recap DOM is still mounted.
   * @param {Object} payload
   */
  applyRankedResult(payload) {
    if (!payload || typeof payload !== 'object') return;
    if (!this._lastData) this._lastData = {};
    this._lastData.ranked = payload;
    this._lastData.rankedPending = false;
    const panel = document.getElementById('gameEndRanked');
    if (!panel) return; // recap torn down or not yet shown
    this._fillRankedPanel(panel, payload, false);
  },

  _buildRankedPanel(ranked, pending) {
    const panel = document.createElement('div');
    panel.id = 'gameEndRanked';
    panel.style.cssText = `
      background: rgba(50, 50, 50, 0.8);
      padding: 20px 40px;
      border-radius: 10px;
      margin-bottom: 30px;
      border: 2px solid #d4af37;
      min-width: 280px;
    `;
    this._fillRankedPanel(panel, ranked, pending);
    return panel;
  },

  _fillRankedPanel(panel, ranked, pending) {
    panel.innerHTML = '';
    const heading = document.createElement('div');
    heading.style.cssText = 'font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #d4af37; margin-bottom: 10px;';
    heading.textContent = 'Ranked';
    panel.appendChild(heading);

    if (pending || !ranked) {
      const wait = document.createElement('div');
      wait.textContent = 'Calculating rank…';
      wait.style.cssText = 'color: #ddd; font-size: 18px;';
      panel.appendChild(wait);
      return;
    }

    const line = (label, value) => {
      const row = document.createElement('div');
      row.style.cssText = 'font-size: 18px; line-height: 1.8; color: #fff;';
      const strong = document.createElement('strong');
      strong.textContent = `${label}: `;
      row.append(strong, document.createTextNode(String(value)));
      return row;
    };

    const b = ranked.breakdown || {};
    const delta = ranked.rpDelta;
    const deltaText = (delta > 0 ? '+' : '') + String(delta);
    panel.append(
      line('Placement', `${b.placement ?? '—'} (${b.placementRp >= 0 ? '+' : ''}${b.placementRp ?? 0} RP)`),
      line('Kills', `${b.killsRp >= 0 ? '+' : ''}${b.killsRp ?? 0} RP`),
      line('Damage', `${b.damageRp >= 0 ? '+' : ''}${b.damageRp ?? 0} RP`),
      line('Entry cost', `−${b.entryCostRp ?? 0} RP`),
      line('Total', `${deltaText} RP`),
      line('RP', ranked.rp)
    );

    const tierLine = document.createElement('div');
    tierLine.style.cssText = 'font-size: 18px; line-height: 1.8; color: #fff;';
    const tierLabel = document.createElement('strong');
    tierLabel.textContent = 'Tier: ';
    let tierText;
    if (ranked.provisional) {
      const total = ranked.provisionalTotal || 5;
      const x = total - (ranked.provisionalRemaining ?? 0);
      tierText = `Placement match ${x} of ${total}`;
    } else {
      tierText = ranked.tierName || ranked.tier || '—';
    }
    tierLine.append(tierLabel, document.createTextNode(tierText));
    panel.appendChild(tierLine);

    if (ranked.promoted && ranked.tierName) {
      const callout = document.createElement('div');
      callout.style.cssText = 'margin-top: 8px; color: #00ff00; font-weight: bold;';
      callout.textContent = `Promoted to ${ranked.tierName}!`;
      panel.appendChild(callout);
    } else if (ranked.demoted && ranked.tierName) {
      const callout = document.createElement('div');
      callout.style.cssText = 'margin-top: 8px; color: #ff8888; font-weight: bold;';
      callout.textContent = `Demoted to ${ranked.tierName}`;
      panel.appendChild(callout);
    }
  },

  /**
   * Show the small persistent "View Recap" badge used while spectating.
   * @private
   */
  _showSpectateBadge() {
    this._hideSpectateBadge();
    const badge = document.createElement('button');
    badge.id = 'gameEndViewRecapBadge';
    badge.textContent = 'View Recap';
    badge.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 10px 20px;
      font-size: 16px;
      font-weight: bold;
      background: rgba(0, 0, 0, 0.75);
      color: white;
      border: 2px solid #888;
      border-radius: 8px;
      cursor: pointer;
      z-index: 2000;
    `;
    badge.onclick = () => this.show(this._lastData);
    document.body.appendChild(badge);
  },

  /**
   * Remove the "View Recap" badge, if present.
   * @private
   */
  _hideSpectateBadge() {
    const badge = document.getElementById('gameEndViewRecapBadge');
    if (badge) {
      badge.remove();
    }
  },

  /**
   * Leftover in-match flags the player hasn't annotated yet.
   * @param {Array<{id: string, summary: string}>} [pendingFlags]
   * @returns {HTMLElement|null}
   * @private
   */
  _buildPendingFlags(pendingFlags) {
    if (!Array.isArray(pendingFlags) || pendingFlags.length === 0) return null;

    const block = document.createElement('div');
    block.id = 'gameEndFlags';
    block.style.cssText = `
      background: rgba(50, 50, 50, 0.8);
      padding: 20px 28px;
      border-radius: 10px;
      margin-bottom: 28px;
      border: 2px solid #666;
      min-width: 360px;
      max-width: 560px;
      width: 90%;
    `;

    const heading = document.createElement('div');
    heading.style.cssText = 'font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #9aa0a6; margin-bottom: 12px;';
    heading.textContent = 'Flags from this match';
    block.appendChild(heading);

    for (const flag of pendingFlags) {
      if (!flag?.id) continue;
      block.appendChild(this._buildFlagRow(flag));
    }

    if (!block.querySelector('[data-flag-id]')) return null;
    return block;
  },

  /**
   * @param {{id: string, summary: string}} flag
   * @returns {HTMLElement}
   * @private
   */
  _buildFlagRow(flag) {
    const row = document.createElement('div');
    row.dataset.flagId = flag.id;
    row.style.cssText = 'margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid #444;';

    const summary = document.createElement('div');
    summary.style.cssText = 'font-size: 14px; color: #ddd; margin-bottom: 8px;';
    summary.textContent = flag.summary || '';
    row.appendChild(summary);

    const input = document.createElement('textarea');
    input.maxLength = 280;
    input.rows = 2;
    input.style.cssText = 'width: 100%; box-sizing: border-box; resize: none; font-size: 14px; padding: 8px;';
    input.addEventListener('keydown', (e) => e.stopPropagation());
    input.addEventListener('keyup', (e) => e.stopPropagation());
    row.appendChild(input);

    const error = document.createElement('div');
    error.style.cssText = 'display: none; color: #ff8888; font-size: 12px; margin-top: 6px;';
    row.appendChild(error);

    const actions = document.createElement('div');
    actions.style.cssText = 'display: flex; gap: 8px; justify-content: flex-end; margin-top: 8px;';

    const skip = document.createElement('button');
    skip.dataset.skip = '';
    skip.textContent = 'Skip';
    skip.style.cssText = 'padding: 6px 14px; cursor: pointer;';
    skip.onclick = () => this._removeFlagRow(row);

    const submit = document.createElement('button');
    submit.dataset.submit = '';
    submit.textContent = 'Submit';
    submit.style.cssText = 'padding: 6px 14px; cursor: pointer;';
    submit.onclick = async () => {
      const note = input.value.trim();
      if (!note) {
        this._removeFlagRow(row);
        return;
      }
      submit.disabled = true;
      skip.disabled = true;
      error.style.display = 'none';
      try {
        if (typeof this.callbacks.onSubmitFlagNote === 'function') {
          await this.callbacks.onSubmitFlagNote(flag.id, note);
        }
        this._removeFlagRow(row);
      } catch {
        submit.disabled = false;
        skip.disabled = false;
        error.textContent = "Couldn't save note";
        error.style.display = 'block';
      }
    };

    actions.appendChild(skip);
    actions.appendChild(submit);
    row.appendChild(actions);
    return row;
  },

  /**
   * @param {HTMLElement} row
   * @private
   */
  _removeFlagRow(row) {
    const id = row.dataset.flagId;
    row.remove();
    if (this._lastData && Array.isArray(this._lastData.pendingFlags)) {
      this._lastData.pendingFlags = this._lastData.pendingFlags.filter((f) => f.id !== id);
    }
    const block = document.getElementById('gameEndFlags');
    if (block && !block.querySelector('[data-flag-id]')) block.remove();
  }
};

// Export for use in other modules
// For now, GameEndScreen is a global object loaded via script tag

export { GameEndScreen };
